import { useEffect, useMemo, useState } from 'react';
import {
    TrendingUp, CheckCircle2, Clock, AlertTriangle, Download, RefreshCw, Loader2,
    CreditCard, Calendar, Percent,
} from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { supabase } from '../lib/supabase';
import { useAppContext } from '../context/useAppContext';
import { formatCurrency, formatDate } from '../utils/formatters';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

interface AnalyticsRow {
    property_id: string;
    property_name: string;
    owner_id: string;
    total_paid_mtd: number;
    total_paid_ytd: number;
    outstanding_balance: number;
    collection_rate: number;
    on_time_rate: number;
    avg_days_late: number;
    paid_count: number;
    pending_count: number;
    overdue_count: number;
}

interface PaymentRow {
    id: string;
    property_id: string;
    tenant_link_id: string | null;
    amount: number;
    status: 'paid' | 'pending' | 'overdue';
    due_date: string | null;
    paid_date: string | null;
    period_month: string | null;
    days_late: number;
}

interface TrendPoint {
    label: string;
    collected: number;
    expected: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function last12Months(): { year: number; month: number; label: string }[] {
    const now = new Date();
    const arr: { year: number; month: number; label: string }[] = [];
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        arr.push({
            year: d.getFullYear(),
            month: d.getMonth(),
            label: `${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
        });
    }
    return arr;
}

function computeTrend(payments: PaymentRow[]): TrendPoint[] {
    const buckets = last12Months().map(m => ({ ...m, collected: 0, expected: 0 }));
    for (const p of payments) {
        const periodDate = p.period_month ? new Date(p.period_month) : p.due_date ? new Date(p.due_date) : null;
        if (!periodDate) continue;
        const bucket = buckets.find(b => b.year === periodDate.getFullYear() && b.month === periodDate.getMonth());
        if (!bucket) continue;
        const amt = Number(p.amount) || 0;
        bucket.expected += amt;
        if (p.status === 'paid') bucket.collected += amt;
    }
    return buckets.map(b => ({ label: b.label, collected: b.collected, expected: b.expected }));
}

function toCsv(rows: Record<string, string | number | null | undefined>[]): string {
    if (!rows.length) return '';
    const headers = Object.keys(rows[0]);
    const esc = (v: unknown) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
}

function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

export default function PaymentAnalyticsPage() {
    const { user } = useAppContext();
    const [analytics, setAnalytics] = useState<AnalyticsRow[]>([]);
    const [payments, setPayments] = useState<PaymentRow[]>([]);
    const [tenantLinks, setTenantLinks] = useState<any[]>([]);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [genMessage, setGenMessage] = useState<string | null>(null);

    const loadAll = async () => {
        if (!user.id) return;
        setLoading(true);
        try {
            // Per-property analytics via SECURITY DEFINER RPC
            const { data: analyticsData, error: aErr } = await supabase.rpc('my_payment_analytics');
            if (aErr) throw aErr;
            setAnalytics((analyticsData || []) as AnalyticsRow[]);

            // Properties this landlord owns (for the trend + recent feed)
            const { data: props } = await supabase
                .from('properties')
                .select('id, name')
                .eq('owner_id', user.id);
            setProperties(props || []);

            const propIds = (props || []).map(p => p.id);
            if (propIds.length === 0) {
                setPayments([]);
                setTenantLinks([]);
                return;
            }

            const [{ data: links }, { data: pays }] = await Promise.all([
                supabase.from('tenant_links').select('id, tenant_name, tenant_email, property_id').in('property_id', propIds),
                supabase
                    .from('rent_payments')
                    .select('id, property_id, tenant_link_id, amount, status, due_date, paid_date, period_month, days_late')
                    .in('property_id', propIds)
                    .order('due_date', { ascending: false }),
            ]);
            setTenantLinks(links || []);
            setPayments((pays || []) as PaymentRow[]);
        } catch (err) {
            console.error('[PaymentAnalyticsPage] load error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [user.id]);

    // ── Aggregates across all properties ───────────────────────────────────
    const totals = useMemo(() => {
        const totalMtd = analytics.reduce((s, r) => s + Number(r.total_paid_mtd), 0);
        const totalOutstanding = analytics.reduce((s, r) => s + Number(r.outstanding_balance), 0);
        // Weighted collection + on-time rates (avoid naive mean of percentages)
        let totalBilled = 0, totalCollected = 0, totalPaid = 0, totalOnTime = 0;
        for (const p of payments) {
            const amt = Number(p.amount) || 0;
            totalBilled += amt;
            if (p.status === 'paid') {
                totalCollected += amt;
                totalPaid += 1;
                if (p.days_late === 0) totalOnTime += 1;
            }
        }
        const collectionRate = totalBilled > 0 ? Math.round((totalCollected / totalBilled) * 100) : 0;
        const onTimeRate = totalPaid > 0 ? Math.round((totalOnTime / totalPaid) * 100) : 0;
        return { totalMtd, totalOutstanding, collectionRate, onTimeRate };
    }, [analytics, payments]);

    const trend = useMemo(() => computeTrend(payments), [payments]);

    const recentPayments = useMemo(() => {
        return [...payments]
            .sort((a, b) => {
                const ad = a.paid_date || a.due_date || '';
                const bd = b.paid_date || b.due_date || '';
                return bd.localeCompare(ad);
            })
            .slice(0, 20);
    }, [payments]);

    const tenantByLinkId = useMemo(() => {
        const map: Record<string, { name: string; email: string }> = {};
        for (const t of tenantLinks) {
            map[t.id] = { name: t.tenant_name || t.tenant_email || '—', email: t.tenant_email || '' };
        }
        return map;
    }, [tenantLinks]);

    const propertyById = useMemo(() => {
        const map: Record<string, string> = {};
        for (const p of properties) map[p.id] = p.name;
        return map;
    }, [properties]);

    // ── Manual generate + export actions ────────────────────────────────────
    const handleGenerateNow = async () => {
        setGenerating(true);
        setGenMessage(null);
        try {
            const { data, error } = await supabase.rpc('generate_monthly_payments');
            if (error) throw error;
            const inserted = Array.isArray(data) && data[0] ? data[0].inserted_count : 0;
            setGenMessage(`${inserted} pending payment${inserted === 1 ? '' : 's'} created for the current month.`);
            await loadAll();
        } catch (err) {
            console.error('[generate_monthly_payments] error:', err);
            setGenMessage('Failed to generate payments. See console.');
        } finally {
            setGenerating(false);
            setTimeout(() => setGenMessage(null), 6000);
        }
    };

    const handleExportCsv = () => {
        const rows = payments.map(p => ({
            property: propertyById[p.property_id] || '',
            tenant: p.tenant_link_id ? tenantByLinkId[p.tenant_link_id]?.name || '' : '',
            period_month: p.period_month || '',
            due_date: p.due_date || '',
            paid_date: p.paid_date || '',
            amount: p.amount,
            status: p.status,
            days_late: p.days_late,
        }));
        downloadCsv(`payment-analytics-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows));
    };

    // ── Render ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="page">
                <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem' }}>
                    <Loader2 className="animate-spin" size={48} color="var(--color-accent)" />
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="page__header">
                <div>
                    <h1 className="page__title">Payment Analytics</h1>
                    <p className="page__subtitle">Collection performance across your portfolio</p>
                </div>
                <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleGenerateNow}
                        disabled={generating}
                        className="btn btn-secondary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        title="Create pending payments for this month's active leases"
                    >
                        {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Generate this month
                    </button>
                    <button
                        onClick={handleExportCsv}
                        className="btn btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        disabled={payments.length === 0}
                    >
                        <Download size={14} />
                        Export CSV
                    </button>
                </div>
            </div>

            {genMessage && (
                <div style={{
                    marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 12,
                    background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)',
                    color: '#166534', fontSize: '0.875rem', fontWeight: 600,
                }}>{genMessage}</div>
            )}

            {/* ── Top stats ──────────────────────────────────────────────── */}
            <div className="overview-grid">
                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Revenue MTD</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                            <TrendingUp size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{formatCurrency(totals.totalMtd)}</div>
                    <div className="overview-card__label">Collected this month</div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Collection Rate</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(37,99,235,0.10)', color: '#2563eb' }}>
                            <Percent size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{totals.collectionRate}%</div>
                    <div className="overview-card__label">Of billed amounts collected</div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">On-Time Rate</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(22,163,74,0.10)', color: '#16a34a' }}>
                            <CheckCircle2 size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{totals.onTimeRate}%</div>
                    <div className="overview-card__label">Paid by due date</div>
                </div>

                <div className="overview-card">
                    <div className="overview-card__header">
                        <span className="overview-card__title">Outstanding</span>
                        <div className="overview-card__icon" style={{ background: 'rgba(220,38,38,0.10)', color: '#dc2626' }}>
                            <AlertTriangle size={18} />
                        </div>
                    </div>
                    <div className="overview-card__value">{formatCurrency(totals.totalOutstanding)}</div>
                    <div className="overview-card__label">Pending + overdue</div>
                </div>
            </div>

            {/* ── Revenue trend chart ────────────────────────────────────── */}
            <div style={{
                background: '#fff', borderRadius: 12, padding: '1.5rem',
                border: '1px solid var(--color-border)', marginBottom: '1.75rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Revenue Trend</h3>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            Collected vs expected over the last 12 months
                        </p>
                    </div>
                </div>
                <div style={{ width: '100%', height: 300 }}>
                    {payments.length === 0 ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
                            color: 'var(--color-text-tertiary)', fontSize: '0.875rem',
                        }}>
                            No payment history yet — use "Generate this month" to create pending rows.
                        </div>
                    ) : (
                        <ResponsiveContainer>
                            <BarChart data={trend}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false}
                                    tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                                <Tooltip
                                    formatter={(value) => formatCurrency(Number(value) || 0)}
                                    contentStyle={{ borderRadius: 10, border: '1px solid var(--color-border)', fontSize: '0.8125rem' }}
                                />
                                <Legend wrapperStyle={{ fontSize: '0.8125rem' }} />
                                <Bar dataKey="expected" name="Expected" fill="#2563eb" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                                <Bar dataKey="collected" name="Collected" fill="#16a34a" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* ── Per-property breakdown ─────────────────────────────────── */}
            <div className="data-panel" style={{ marginBottom: '1.75rem' }}>
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Per-property breakdown</h3>
                </div>
                {analytics.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                        No properties yet.
                    </div>
                ) : (
                    <div className="responsive-table">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Property</th>
                                    <th>MTD</th>
                                    <th>Collection</th>
                                    <th>Outstanding</th>
                                    <th>On-time</th>
                                    <th>Overdue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.map(row => (
                                    <tr key={row.property_id}>
                                        <td style={{ fontWeight: 700 }}>{row.property_name}</td>
                                        <td>{formatCurrency(Number(row.total_paid_mtd))}</td>
                                        <td>
                                            <span className={`badge badge--${Number(row.collection_rate) >= 90 ? 'green' : Number(row.collection_rate) >= 70 ? 'yellow' : 'red'}`}>
                                                {Number(row.collection_rate).toFixed(0)}%
                                            </span>
                                        </td>
                                        <td style={{ color: Number(row.outstanding_balance) > 0 ? '#dc2626' : 'inherit' }}>
                                            {formatCurrency(Number(row.outstanding_balance))}
                                        </td>
                                        <td>{Number(row.on_time_rate).toFixed(0)}%</td>
                                        <td>
                                            {row.overdue_count > 0 ? (
                                                <span className="badge badge--red">{row.overdue_count}</span>
                                            ) : (
                                                <span className="badge badge--gray">0</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Recent payments feed ───────────────────────────────────── */}
            <div className="data-panel">
                <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Recent payments</h3>
                </div>
                {recentPayments.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                        <CreditCard size={40} opacity={0.2} style={{ marginBottom: '0.75rem' }} />
                        <div>No payments recorded yet.</div>
                    </div>
                ) : (
                    <div className="responsive-table">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Tenant</th>
                                    <th>Property</th>
                                    <th>Amount</th>
                                    <th>Due</th>
                                    <th>Paid</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentPayments.map(p => {
                                    const tenant = p.tenant_link_id ? tenantByLinkId[p.tenant_link_id] : null;
                                    return (
                                        <tr key={p.id}>
                                            <td style={{ fontWeight: 600 }}>{tenant?.name || '—'}</td>
                                            <td>{propertyById[p.property_id] || '—'}</td>
                                            <td style={{ fontWeight: 700 }}>{formatCurrency(Number(p.amount))}</td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {p.due_date ? formatDate(p.due_date) : '—'}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>
                                                {p.paid_date ? formatDate(p.paid_date) : '—'}
                                            </td>
                                            <td>
                                                {p.status === 'paid' && (
                                                    <span className="badge badge--green"><CheckCircle2 size={12} /> Paid</span>
                                                )}
                                                {p.status === 'pending' && (
                                                    <span className="badge badge--yellow"><Clock size={12} /> Pending</span>
                                                )}
                                                {p.status === 'overdue' && (
                                                    <span className="badge badge--red"><AlertTriangle size={12} /> Overdue</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Footer hint */}
            <div style={{ marginTop: '1.25rem', fontSize: '0.75rem', color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={12} />
                Pending payments are auto-generated on the 1st of each month. Overdue status refreshes daily.
            </div>
        </div>
    );
}
